// AI_GOOD: UI components extracted for better organization
import React, { useState } from "react";
import { ExternalLink, Loader2, Languages } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { 
  sectionIconMap,
  DefaultSectionIcon,
  languageMap,
  ALL_POSSIBLE_LANGUAGES,
} from "@/pages/MissingArticle/types";

// AI_GOOD: DetailItem component preserved exactly as in original
export interface DetailItemProps {
  label: string;
  value?: string | null;
  link?: string | null;
  children?: React.ReactNode;
  icon?: React.ReactElement | undefined;
}

export const DetailItem: React.FC<DetailItemProps> = ({ 
  label, 
  value, 
  link, 
  children, 
  icon 
}) => {
  if (!value && !children && !link) return null;

  let iconWithClasses: React.ReactElement | undefined = undefined;
  if (icon && React.isValidElement(icon)) {
    const existingIconProps = icon.props as any;
    const existingClassName = existingIconProps.className || "";
    iconWithClasses = React.cloneElement(icon as React.ReactElement<any>, {
      className: `${existingClassName} h-4 w-4 mr-2 text-base-content/60`.trim(),
    });
  }

  return (
    <div className="py-2">
      <dt className="flex items-center text-sm font-medium text-base-content/70">
        {iconWithClasses}
        {label}
      </dt>
      <dd className="mt-1 text-sm text-base-content">
        {link && value ? (
          <a
            href={link ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex link items-center link-primary hover:underline"
          >
            {value} <ExternalLink size={14} className="ml-1" />
          </a>
        ) : value ? (
          value
        ) : (
          children || <span className="text-base-content/50">N/A</span>
        )}
      </dd>
    </div>
  );
};

// AI_GOOD: Section icon getter preserved exactly
export const getSectionIcon = (title?: string | null) => {
  const lowerTitle = title?.toLowerCase();
  return lowerTitle && sectionIconMap[lowerTitle]
    ? sectionIconMap[lowerTitle]
    : DefaultSectionIcon;
};

// AI_GOOD: Version selector component
export interface VersionSelectorProps {
  allUniqueVersionsSorted: number[];
  selectedDisplayVersion: number | null;
  onVersionSelect: (version: number) => void;
}

export const VersionSelector: React.FC<VersionSelectorProps> = ({
  allUniqueVersionsSorted,
  selectedDisplayVersion,
  onVersionSelect,
}) => {
  if (allUniqueVersionsSorted.length === 0) {
    return (
      <div className="p-2 text-xs text-base-content/70">
        No versions available.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap justify-end gap-1 sm:gap-2">
      {allUniqueVersionsSorted.map((version) => (
        <button
          key={version}
          className={`btn btn-sm ${selectedDisplayVersion === version ? "btn-primary text-primary-content transition-all duration-300" : "btn-ghost"}`}
          onClick={() => {
            onVersionSelect(version);
            (document.activeElement as HTMLElement)?.blur();
          }}
        >
          Version {version}
        </button>
      ))}
    </div>
  );
};

// AI_GOOD: Language selector component
export interface LanguageSelectorProps {
  selectedDisplayVersion: number | null;
  selectedDisplayLanguage: string;
  currentDisplayDoc?: any;
  pendingTranslations: Record<string, boolean>;
  isLanguageEnabledForVersion: (lang: string, version: number | null) => boolean;
  onLanguageSelect: (lang: string) => void;
  onTranslateRequest: (lang: string, version: number, fromLang: string, content: string) => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedDisplayVersion,
  selectedDisplayLanguage,
  currentDisplayDoc,
  pendingTranslations,
  isLanguageEnabledForVersion,
  onLanguageSelect,
  onTranslateRequest,
}) => {
  const [confirmingLanguage, setConfirmingLanguage] = useState<string | null>(null);

  if (selectedDisplayVersion === null) {
    return null;
  }
  
  const handleTranslationConfirmed = () => {
    if (confirmingLanguage && selectedDisplayVersion !== null && currentDisplayDoc?.content) {
      onTranslateRequest(
        confirmingLanguage, 
        selectedDisplayVersion,
        selectedDisplayLanguage,
        currentDisplayDoc.content
      );
      setConfirmingLanguage(null);
    }
  };

  return (
    <div className="mt-1 flex flex-wrap justify-end gap-1 sm:mt-2 sm:gap-2">
      {/* Confirmation Modal */}
      <dialog id="translation_confirm_modal" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Confirm Translation</h3>
          <p className="py-4">
            Are you sure you want to translate this content to {confirmingLanguage ? (languageMap[confirmingLanguage]?.name || confirmingLanguage.toUpperCase()) : ''}?
          </p>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn btn-ghost mr-2" onClick={() => setConfirmingLanguage(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleTranslationConfirmed}>Translate</button>
            </form>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setConfirmingLanguage(null)}>close</button>
        </form>
      </dialog>

      {ALL_POSSIBLE_LANGUAGES.map((lang) => {
        const isEnabled = isLanguageEnabledForVersion(lang, selectedDisplayVersion);
        const canTranslate = !isEnabled && currentDisplayDoc?.content;

        return (
          <div
            key={lang}
            className={`${canTranslate ? 'tooltip tooltip-bottom cursor-pointer' : ''}`}
          >
            {canTranslate && (
              <span className="tooltip-content rounded-md bg-neutral px-2 py-1 text-xs text-neutral-content shadow-lg">
                <div className="flex items-center">
                  <Languages size={14} className="mr-1" />
                  {pendingTranslations[`${selectedDisplayVersion}_${lang}`] ? (
                      <span>Translating to {languageMap[lang]?.name || lang.toUpperCase()}</span>
                  ) : (
                      <span>Translate to {languageMap[lang]?.name || lang.toUpperCase()}</span>
                  )}
                </div>
              </span>
            )}
            <button
              className={`btn btn-xs sm:btn-sm 
                        ${selectedDisplayLanguage === lang && isEnabled ? "btn-primary text-primary-content" : ""}
                        ${!canTranslate && selectedDisplayLanguage !== lang ? "btn-info text-info-content" : ""}
                        ${!isEnabled && !currentDisplayDoc?.content ? "btn-disabled opacity-40 cursor-not-allowed" : ""} 
                        ${canTranslate ? "opacity-60 hover:opacity-100" : ""}
                        ${isEnabled && selectedDisplayLanguage !== lang ? "" : ""}
                        ${pendingTranslations[`${selectedDisplayVersion}_${lang}`] ? "animate-pulse transition-all duration-300 bg-gradient-to-r text-primary-content from-primary to-secondary" : ""}
                      `}
              onClick={() => {
                if (isEnabled) {
                  onLanguageSelect(lang);
                } else if (canTranslate) {
                  setConfirmingLanguage(lang);
                  const modal = document.getElementById('translation_confirm_modal') as HTMLDialogElement;
                  if (modal) modal.showModal();
                }
                (document.activeElement as HTMLElement)?.blur();
              }}
            >
              {languageMap[lang]?.flagComponent}
              {languageMap[lang]?.name || lang.toUpperCase()}
            </button>
          </div>
        );
      })}
    </div>
  );
};

// AI_GOOD: Content display header component
export interface ContentDisplayHeaderProps {
  selectedDisplayVersion: number;
  selectedDisplayLanguage: string;
  currentDisplayDoc?: any;
  onViewPrompt: (prompt: string) => void;
}

export const ContentDisplayHeader: React.FC<ContentDisplayHeaderProps> = ({
  selectedDisplayVersion,
  selectedDisplayLanguage,
  currentDisplayDoc,
  onViewPrompt,
}) => {
  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
              Version {selectedDisplayVersion}
          </span>
          <span className="text-sm">
              {languageMap[selectedDisplayLanguage]?.flagComponent}
          </span>
      </div>
      
      <div className="flex items-center gap-2">
        {currentDisplayDoc?.input_prompt && (
          <button
            className="tooltip btn tooltip-left text-base-content/60 btn-ghost btn-xs"
            data-tip="View Prompt"
            onClick={() => onViewPrompt(currentDisplayDoc.input_prompt || "No prompt recorded.")}
          >
            <ExternalLink size={14} />
          </button>
        )}
        <span className="text-xs text-base-content/70">
          Generated by:{" "}
          {currentDisplayDoc?.generated_by?.username || "System"}{" "}
          on{" "}
          {formatDistanceToNow(
            parseISO(currentDisplayDoc?.timestamp || new Date().toISOString()),
            { addSuffix: true },
          )}
        </span>
      </div>
    </div>
  );
};

// AI_GOOD: Loading spinner component
export interface LoadingSpinnerProps {
  message?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  message = "Loading..." 
}) => {
  return (
    <div className="flex h-screen items-center justify-center">
      <Loader2 className="h-12 w-12 animate-spin text-primary" />
      <p className="ml-4 text-lg">{message}</p>
    </div>
  );
};

// AI_GOOD: Error display component
export interface ErrorDisplayProps {
  error: string;
  onBack?: () => void;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onBack }) => {
  return (
    <div
      role="alert"
      className="mx-auto mt-8 alert max-w-3xl alert-error shadow-lg"
    >
      <div>
        <h3 className="font-bold">Error Loading Article</h3>
        <p className="text-sm">{error}</p>
      </div>
      {onBack && (
        <button
          onClick={onBack}
          className="btn mt-4 btn-outline btn-sm md:mt-0"
        >
          Back
        </button>
      )}
    </div>
  );
};

// AI_GOOD: File upload component
export interface FileUploadItemProps {
  file: {
    id: string;
    file: File;
  };
  onRemove: (id: string) => void;
}

export const FileUploadItem: React.FC<FileUploadItemProps> = ({ file, onRemove }) => {
  return (
    <div className="flex items-center justify-between rounded bg-base-200 p-1 text-xs">
      <span>{file.file.name}</span>
      <button
        className="btn text-error btn-ghost btn-xs"
        onClick={() => onRemove(file.id)}
      >
        ✕
      </button>
    </div>
  );
}; 